import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../components/Loader";
import ViewModal from "../components/ViewModal";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const apiKey = process.env.REACT_APP_API_KEY;

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

export default function FollowUps() {
  const role = Cookies.get("user_role");
  const username = Cookies.get("username");
  const isAdmin = role === "Administrator";

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  const GET_URL = BASE_URL + "/getfollowups";
  const ADD_URL = BASE_URL + "/addfollowup";

  const fetchRows = () => {
    setLoading(true);
    axios
      .get(GET_URL, {
        headers: {
          Authorization: apiKey,
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        setRows(Array.isArray(res.data) ? res.data : []);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to load follow ups.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleRows = useMemo(() => {
    if (isAdmin) return rows;
    return rows.filter((r) => String(r.EmployeeName || "") === String(username || ""));
  }, [rows, isAdmin, username]);

  const filteredRows = useMemo(() => {
    const term = String(searchTerm || "").trim().toLowerCase();
    if (!term) return visibleRows;
    return visibleRows.filter((r) =>
      [
        r.EmployeeName,
        r.ClientName,
        r.ClientEmail,
        r.PhoneNumber,
        r.BusinessName,
        r.EmployeeEmail,
        r.ClientReply,
        r.Created_At,
        r.Id,
      ]
        .filter((v) => v !== null && v !== undefined)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [visibleRows, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, visibleRows.length]);

  const sortedRows = useMemo(() => {
    return filteredRows.slice().sort((a, b) => (b.Id || 0) - (a.Id || 0));
  }, [filteredRows]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const pageRows = sortedRows.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (p) => {
    const next = Math.min(totalPages, Math.max(1, p));
    setCurrentPage(next);
  };

  const handleAdd = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const data = {
      employeeName: username,
      clientName: formData.get("clientName"),
      clientEmail: formData.get("clientEmail"),
      phoneNumber: formData.get("phoneNumber"),
      businessName: formData.get("businessName"),
      employeeEmail: formData.get("employeeEmail"),
      clientReply: formData.get("clientReply"),
    };

    const missing = Object.entries(data).filter(([_, v]) => !v);
    if (missing.length > 0) {
      toast.error("All fields are required!");
      return;
    }

    axios
      .post(ADD_URL, data, {
        headers: {
          Authorization: apiKey,
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        toast.success("Follow up added successfully.");
        e.target.reset();
        fetchRows();
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to add follow up.");
      });
  };

  const handleDelete = (id) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this follow up?")) return;

    axios
      .delete(BASE_URL + "/deletefollowup/" + id, {
        headers: {
          Authorization: apiKey,
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        toast.success("Follow up deleted.");
        fetchRows();
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to delete follow up.");
      });
  };

  const openDetails = (row) => {
    setSelected(row);
    setDetailsOpen(true);
  };

  if (loading) return <Loader />;

  return (
    <div className="container">
      <ToastContainer />
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <h2 className="text-light mb-0">Follow Up Sheet</h2>
          <small className="text-secondary">
            Showing <b>{filteredRows.length}</b> of <b>{visibleRows.length}</b>
          </small>
        </div>
        <div style={{ minWidth: 260 }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search follow ups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <br />

      {!isAdmin && (
        <>
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <div>
                <div style={{ fontWeight: 700 }}>Add Follow Up</div>
                <small className="text-muted">Only you can add your follow ups</small>
              </div>
            </div>
            <div className="card-body">
            <form onSubmit={handleAdd}>
              <div className="row">
                <div className="col-sm-6">
                  <input
                    type="text"
                    className="form-control"
                    value={username || ""}
                    disabled
                  />
                  <small className="text-muted">Employee Name (auto)</small>
                </div>
                <div className="col-sm-6">
                  <input
                    type="email"
                    placeholder="Employee Email"
                    className="form-control"
                    name="employeeEmail"
                    required
                  />
                </div>
              </div>
              <br />
              <div className="row">
                <div className="col-sm-6">
                  <input
                    type="text"
                    placeholder="Client Name"
                    className="form-control"
                    name="clientName"
                    required
                  />
                </div>
                <div className="col-sm-6">
                  <input
                    type="email"
                    placeholder="Client Email"
                    className="form-control"
                    name="clientEmail"
                    required
                  />
                </div>
              </div>
              <br />
              <div className="row">
                <div className="col-sm-6">
                  <input
                    type="text"
                    placeholder="Phone"
                    className="form-control"
                    name="phoneNumber"
                    required
                  />
                </div>
                <div className="col-sm-6">
                  <input
                    type="text"
                    placeholder="Business Name"
                    className="form-control"
                    name="businessName"
                    required
                  />
                </div>
              </div>
              <br />
              <div className="row">
                <div className="col-sm-12">
                  <textarea
                    placeholder="Client Reply"
                    className="form-control"
                    name="clientReply"
                    rows={4}
                    required
                  />
                </div>
              </div>
              <br />
              <button className="btn btn-primary">Add Follow Up</button>
            </form>
            </div>
          </div>
          <br />
        </>
      )}

      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-dark table-striped table-hover align-middle mb-0">
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              <th>Employee Name</th>
              <th>Client Name</th>
              <th>Client Email</th>
              <th>Phone</th>
              <th>Business Name</th>
              <th>Employee Email</th>
              <th>Client Reply</th>
              <th>Create At</th>
              <th>Delete</th>
            </tr>
              </thead>
              <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-4">
                  No follow ups found.
                </td>
              </tr>
            ) : (
              pageRows.map((r) => (
                  <tr
                    key={r.Id}
                    style={{ cursor: "pointer" }}
                    onClick={() => openDetails(r)}
                    title="Click to view full details"
                  >
                    <td>{r.EmployeeName}</td>
                    <td style={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.ClientName}
                    </td>
                    <td style={{ maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.ClientEmail}
                    </td>
                    <td>{r.PhoneNumber}</td>
                    <td style={{ maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.BusinessName}
                    </td>
                    <td style={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.EmployeeEmail}
                    </td>
                    <td style={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {String(r.ClientReply || "").replace(/\s+/g, " ").trim()}
                    </td>
                    <td>{formatDate(r.Created_At)}</td>
                    <td>
                      {isAdmin ? (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(r.Id);
                          }}
                        >
                          Delete
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))
            )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {totalPages > 1 && (
        <>
          <br />
          <nav>
            <ul className="pagination" style={{ display: "flex", flexWrap: "wrap" }}>
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => goToPage(currentPage - 1)}>
                  Prev
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li
                  key={i}
                  className={`page-item ${i + 1 === currentPage ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => goToPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => goToPage(currentPage + 1)}>
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </>
      )}

      <ViewModal
        open={detailsOpen}
        title={selected ? `Follow Up Details #${selected.Id}` : "Follow Up Details"}
        onClose={() => setDetailsOpen(false)}
      >
        {selected ? (
          <div className="container-fluid">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="p-3 border rounded">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Employee
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{selected.EmployeeName}</div>
                  <div className="text-muted" style={{ marginTop: 6 }}>
                    {selected.EmployeeEmail}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 border rounded">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Client
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{selected.ClientName}</div>
                  <div className="text-muted" style={{ marginTop: 6 }}>
                    {selected.ClientEmail}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <span className="badge text-bg-dark">{selected.PhoneNumber}</span>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="p-3 border rounded">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Business Name
                  </div>
                  <div style={{ fontWeight: 700 }}>{selected.BusinessName}</div>
                </div>
              </div>

              <div className="col-12">
                <div className="p-3 border rounded">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Client Reply
                  </div>
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {selected.ClientReply}
                  </div>
                </div>
              </div>

              <div className="col-12 d-flex justify-content-between align-items-center">
                <div className="text-muted">
                  Created: <b>{formatDate(selected.Created_At)}</b>
                </div>
                {isAdmin && (
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      setDetailsOpen(false);
                      handleDelete(selected.Id);
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </ViewModal>
    </div>
  );
}

